// src/pages/Facturacion.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';
import GlassCard from '../components/UI/GlassCard';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { 
  ShoppingCart, 
  Search, 
  Plus, 
  Trash2, 
  X,
  DollarSign, 
  FileText, 
  Download,
  TrendingUp,
  CreditCard,
  User,
  Calculator,
  ChevronRight,
  Eye,
  Package,
  Zap,
  Tag,
  UserPlus,
  ZapOff,
  Mail,
  Send
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import { useLocation } from 'react-router-dom';

const SafeTippy = ({ children, ...props }) => {
  const [ref, setRef] = useState(null);
  return (
    <>
      <Tippy {...props} reference={ref} />
      {React.cloneElement(React.Children.only(children), { ref: setRef })}
    </>
  );
};

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-PR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const Facturacion = () => {
    const { can } = useAuth();
    const location = useLocation();
    const [facturas, setFacturas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPos, setShowPos] = useState(false);
    const [posTab, setPosTab] = useState('servicios'); // 'servicios' o 'productos'
    
    // Email State
    const [emailDestino, setEmailDestino] = useState('');
    const [enviandoEmail, setEnviandoEmail] = useState(false);
    const [selectedFacturaData, setSelectedFacturaData] = useState(null);
    
    // POS State
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [searchClient, setSearchClient] = useState('');
    const [searchItem, setSearchItem] = useState('');
    const [cart, setCart] = useState([]);
    const [metodoPago, setMetodoPago] = useState('Efectivo');
    const [citaId, setCitaId] = useState(null);
    const [showQuickClient, setShowQuickClient] = useState(false);
    const [quickClientForm, setQuickClientForm] = useState({ nombre: '', apellido: '', telefono: '' });

    const [stats, setStats] = useState({ total: 0, count: 0, avg: 0 });
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [settings, setSettings] = useState({});

    const fetchFacturas = useCallback(async () => {
        try {
            const response = await axios.get('/backend/api/facturacion/list.php');
            if (response.data.success) {
                setFacturas(response.data.data.facturas);
                setStats(response.data.data.stats);
            }
        } catch (error) {
            toast.error('Error al cargar historial');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBaseData = useCallback(async () => {
        try {
            const [cRes, sRes, pRes, configRes] = await Promise.all([
                axios.get('/backend/api/clientes/index.php'),
                axios.get('/backend/api/servicios/index.php'),
                axios.get('/backend/api/productos/index.php'),
                axios.get('/backend/api/configuracion/index.php')
            ]);
            
            if (cRes.data.success) setClientes(cRes.data.data);
            if (sRes.data.success) setServicios(sRes.data.data);
            if (pRes.data.success) setProductos(pRes.data.data);
            if (configRes.data.success) setSettings(configRes.data.data);
            
            // Lógica de Pre-llenado desde Navigation State
            if (location.state && location.state.prefillClienteId && cRes.data.success) {
                const cliente = cRes.data.data.find(c => c.id === parseInt(location.state.prefillClienteId));
                if (cliente) {
                    setSelectedCliente(cliente);
                    setSearchClient(cliente.nombre + ' ' + (cliente.apellido || ''));
                    setCitaId(location.state.cita_id);
                    // Asegurar que los items de cita tengan servicio_id
                    const preItems = (location.state.items || []).map(it => ({
                        ...it,
                        servicio_id: it.id, // En la cita, id es el servicio_id
                        aplica_ivu: 1
                    }));
                    setCart(preItems);
                    setShowPos(true);
                    window.history.replaceState({}, document.title);
                }
            }
        } catch (error) {
            toast.error('Error al cargar datos base');
        }
    }, [location.state]);

    useEffect(() => {
        fetchFacturas();
        fetchBaseData();
        const handleStartBilling = () => setShowPos(true);
        window.addEventListener('start-billing', handleStartBilling);
        return () => window.removeEventListener('start-billing', handleStartBilling);
    }, [fetchFacturas, fetchBaseData]);

    const selectWalkIn = () => {
        const walkin = clientes.find(c => c.nombre === 'Publico' && c.apellido === 'General');
        if (walkin) {
            setSelectedCliente(walkin);
            setSearchClient('Publico General');
            toast.success('Modo Walk-In Activado');
        } else {
            toast.error('Cliente General no configurado');
        }
    };

    const handleQuickClientSave = async () => {
        if (!quickClientForm.nombre || !quickClientForm.telefono) return toast.error('Nombre y Teléfono son vitales');
        try {
            const response = await axios.post('/backend/api/clientes/index.php', quickClientForm);
            if (response.data.success) {
                const newClient = { ...quickClientForm, id: response.data.data.id };
                setClientes([newClient, ...clientes]);
                setSelectedCliente(newClient);
                setSearchClient(`${newClient.nombre} ${newClient.apellido}`);
                setShowQuickClient(false);
                setQuickClientForm({ nombre: '', apellido: '', telefono: '' });
                toast.success('Cliente registrado y seleccionado');
            }
        } catch (error) {
            toast.error('Error al registrar cliente');
        }
    };

    const addToCart = (item, type) => {
        const isProduct = type === 'producto';
        setCart([...cart, {
            [isProduct ? 'producto_id' : 'servicio_id']: item.id,
            descripcion: item.nombre,
            precio_unitario: isProduct ? item.precio_venta : item.precio_base,
            cantidad: 1,
            aplica_ivu: parseInt(item.aplica_ivu || 1),
            type: type
        }]);
        toast.success(`${item.nombre} añadido`);
    };

    const removeFromCart = (index) => {
        const newCart = [...cart];
        newCart.splice(index, 1);
        setCart(newCart);
    };

    const calculateCart = () => {
        let subtotal = 0;
        let ivuEstatal = 0;
        let ivuMunicipal = 0;

        const rate1 = parseFloat(settings.ivu_rate || 0.115);
        const rate2 = parseFloat(settings.ivu_municipal || 0.01);

        cart.forEach(item => {
            const lineSub = parseFloat(item.precio_unitario) * item.cantidad;
            subtotal += lineSub;
            if (item.aplica_ivu) {
                ivuEstatal += lineSub * rate1;
                ivuMunicipal += lineSub * rate2;
            }
        });
        
        return { subtotal, ivuEstatal, ivuMunicipal, total: subtotal + ivuEstatal + ivuMunicipal };
    };

    const handleProcessPayment = async () => {
        if (!selectedCliente) return toast.error('Selecciona un cliente');
        if (cart.length === 0) return toast.error('El carrito está vacío');

        const totals = calculateCart();
        const result = await Swal.fire({
            title: '¿Confirmar Pago?',
            text: `Total a cobrar: ${formatCurrency(totals.total)}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, cobrar ahora',
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff',
            confirmButtonColor: '#8b5cf6'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.post('/backend/api/facturas/index.php', {
                    cliente_id: selectedCliente.id,
                    cita_id: citaId,
                    metodo_pago: metodoPago,
                    items: cart,
                    subtotal: totals.subtotal,
                    ivu_estatal: totals.ivuEstatal,
                    ivu_municipal: totals.ivuMunicipal,
                    total: totals.total
                });

                if (response.data.success) {
                    toast.success(`Factura generada con éxito`);
                    setShowPos(false);
                    setCart([]);
                    setSelectedCliente(null);
                    setCitaId(null);
                    fetchFacturas();

                    // Previsualización
                    const token = localStorage.getItem('token');
                    setPreviewUrl(`/backend/api/facturacion/pdf.php?id=${response.data.data.id}&token=${token}`);
                    setShowPreview(true);
                    setSelectedFacturaData(response.data.data);
                    setEmailDestino(selectedCliente.email || '');
                }
            } catch (error) {
                toast.error('Error al procesar cobro');
            }
        }
    };

    const isMobileDevice = () => {
        return (window.innerWidth < 1024) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const handlePreviewInvoice = (f) => {
        const token = localStorage.getItem('token');
        const url = `/backend/api/facturacion/pdf.php?id=${f.id}&token=${token}`;
        
        if (isMobileDevice()) {
            toast.success('Abriendo factura en visor nativo... 🧾');
            window.open(url, '_blank');
            return;
        }

        setPreviewUrl(url);
        setShowPreview(true);
        setSelectedFacturaData(f);
        setEmailDestino(f.cliente_email || '');
    };

    const handleSendEmail = async () => {
        if (!emailDestino) return toast.error('Ingrese un email de destino');
        
        setEnviandoEmail(true);
        try {
            const response = await axios.post('/backend/api/facturacion/enviar_email.php', {
                factura_id: selectedFacturaData.id,
                email: emailDestino
            });

            if (response.data.success) {
                Swal.fire({
                    title: '¡Factura Enviada! 📧',
                    text: 'El comprobante ha sido entregado exitosamente al cliente.',
                    icon: 'success',
                    background: 'rgba(15, 23, 42, 0.95)',
                    color: '#fff',
                    confirmButtonColor: '#8b5cf6',
                    timer: 3000
                });
                setShowPreview(false);
            }
        } catch (error) {
            toast.error('Error al enviar el comprobante');
        } finally {
            setEnviandoEmail(false);
        }
    };

    if (loading) return (
        <div className="h-screen w-full flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
    );

    const cartTotals = calculateCart();

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-black flex items-center gap-3">
                        <ShoppingCart className="text-primary-light" size={42} /> 
                        Hub de <span className="text-primary-light italic font-medium">Cobros</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm font-medium flex items-center gap-2">
                        <TrendingUp size={14} className="text-emerald-400" />
                        Venta de servicios y productos retail en un solo lugar
                    </p>
                </div>
                
                {can('Ventas', 'crear') && (
                    <button 
                      onClick={() => setShowPos(true)}
                      className="premium-btn-primary flex items-center gap-3 px-8 shadow-xl shadow-primary/20"
                    >
                        <Plus size={20} />
                        <span>Nueva Factura (POS)</span>
                    </button>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <GlassCard className="flex items-center gap-6">
                    <div className="bg-emerald-500/20 p-5 rounded-3xl border border-emerald-500/10"><DollarSign size={28} className="text-emerald-400" /></div>
                    <div>
                        <span className="text-[10px] uppercase font-black text-white/30 block mb-1">Volumen de Ingresos</span>
                        <span className="text-3xl font-black text-white">{formatCurrency(stats.total)}</span>
                    </div>
                </GlassCard>
                <GlassCard className="flex items-center gap-6">
                    <div className="bg-primary/20 p-5 rounded-3xl border border-primary/10"><FileText size={28} className="text-primary-light" /></div>
                    <div>
                        <span className="text-[10px] uppercase font-black text-white/30 block mb-1">Facturas Emitidas</span>
                        <span className="text-3xl font-black text-white">{stats.count}</span>
                    </div>
                </GlassCard>
                <GlassCard className="flex items-center gap-6">
                    <div className="bg-secondary/20 p-5 rounded-3xl border border-secondary/10"><Calculator size={28} className="text-secondary" /></div>
                    <div>
                        <span className="text-[10px] uppercase font-black text-white/30 block mb-1">Ticket Promedio</span>
                        <span className="text-3xl font-black text-white">{formatCurrency(stats.avg)}</span>
                    </div>
                </GlassCard>
            </div>

            <GlassCard padding="0" className="overflow-hidden">
                <Table 
                    headers={['Fólio', 'Fecha', 'Cliente', 'Cobrado por', 'Estado', 'Total']}
                    data={facturas.map(f => ({
                        _raw: f,
                        nro: <span className="font-black text-primary-light">#{f.numero_factura}</span>,
                        fecha: formatDate(f.fecha_factura),
                        cliente: <span className="font-bold">{f.cliente_nombre} {f.cliente_apellido}</span>,
                        atendido: <span className="text-xs text-white/40">{f.empleado_nombre}</span>,
                        estado: <span className="text-[10px] font-black uppercase text-emerald-400">PAGADA</span>,
                        total: <span className="font-black text-lg text-emerald-400">{formatCurrency(f.total)}</span>
                    }))}
                    actions={(row) => (
                        <div className="flex gap-2">
                             <SafeTippy content="Ver Comprobante">
                                <button onClick={() => handlePreviewInvoice(row._raw)} className="glass p-2 rounded-xl text-primary-light hover:bg-white/10 transition-all"><Eye size={18} /></button>
                             </SafeTippy>
                        </div>
                    )}
                />
            </GlassCard>

            {/* MODAL POS (FULLSCREEN EXPERIENCE) */}
            <Modal 
                show={showPos} 
                onClose={() => { setShowPos(false); setSelectedCliente(null); setCitaId(null); }} 
                title="Punto de Venta Elite — PawSpa Retail"
                size="full"
                footer={(
                    <div className="flex justify-between items-center w-full">
                        <div className="flex gap-10">
                            <div><span className="text-[10px] font-black text-white/30 block uppercase">Subtotal</span><span className="text-xl font-bold">{formatCurrency(cartTotals.subtotal)}</span></div>
                            <div className="border-x border-white/5 px-10">
                                <span className="text-[10px] font-black text-primary-light block uppercase">Taxes (Puerto Rico)</span>
                                <span className="text-xl font-bold text-primary-light">{formatCurrency(cartTotals.ivuEstatal + cartTotals.ivuMunicipal)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-8">
                            <div className="text-right">
                                <span className="text-xs font-black text-emerald-400 block uppercase">Neto Total</span>
                                <span className="text-4xl font-black text-white tracking-tighter">{formatCurrency(cartTotals.total)}</span>
                            </div>
                            <button onClick={handleProcessPayment} className="premium-btn-primary px-12 py-5 text-sm shadow-2xl shadow-emerald-500/20 flex items-center gap-3">
                                <CreditCard size={24} /> PROCESAR COBRO
                            </button>
                        </div>
                    </div>
                )}
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 h-[calc(100vh-320px)] overflow-hidden">
                    {/* COLUMNA IZQUIERDA: SELECCIÓN */}
                    <div className="flex flex-col gap-6 overflow-hidden">
                        {/* Selector Cliente */}
                        <div className="space-y-3">
                            <h4 className="text-[10px] font-black uppercase text-white/20 tracking-[2px] flex items-center justify-between">
                                <span className="flex items-center gap-2"><User size={12}/> Cliente Responsable</span>
                                <div className="flex gap-2">
                                    <button onClick={selectWalkIn} className="text-secondary hover:text-white flex items-center gap-1 transition-colors"><Zap size={10}/> Walk-In</button>
                                    <button onClick={() => setShowQuickClient(true)} className="text-primary-light hover:text-white flex items-center gap-1 transition-colors"><UserPlus size={10}/> Nuevo</button>
                                </div>
                            </h4>
                            <div className="relative group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary-light transition-colors" size={18} />
                                <input 
                                    type="text" 
                                    className="input-field pl-12 pr-12 bg-slate-900/40 border-white/5 focus:border-primary-light/30 transition-all" 
                                    placeholder="Filtrar por nombre o teléfono..." 
                                    value={searchClient} 
                                    onChange={e => setSearchClient(e.target.value)} 
                                />
                                {searchClient && (
                                    <button 
                                        onClick={() => {
                                            setSearchClient('');
                                            setSelectedCliente(null);
                                        }}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-red-400 p-1 transition-colors"
                                        title="Clear Search"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                                {clientes.filter(c => `${c.nombre} ${c.apellido}`.toLowerCase().includes(searchClient.toLowerCase())).slice(0, 5).map(c => (
                                    <button key={c.id} onClick={() => setSelectedCliente(c)} className={`shrink-0 flex flex-col p-4 rounded-2xl border transition-all min-w-[150px] ${selectedCliente?.id === c.id ? 'bg-primary/20 border-primary shadow-lg ring-1 ring-primary' : 'bg-white/5 border-white/5 hover:bg-white/10'}`}>
                                        <span className="font-bold text-sm truncate">{c.nombre}</span>
                                        <span className="text-[10px] text-white/30 uppercase truncate">{c.telefono}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Catálogo de Items */}
                        <div className="flex-1 flex flex-col gap-4 overflow-hidden mt-4">
                            <div className="flex justify-between items-center">
                                <h4 className="text-[10px] font-black uppercase text-white/20 tracking-[2px]">Catálogo de Venta</h4>
                                <div className="flex gap-2 bg-white/5 p-1 rounded-xl border border-white/5">
                                    <button onClick={() => setPosTab('servicios')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${posTab === 'servicios' ? 'bg-primary text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>Grooming</button>
                                    <button onClick={() => setPosTab('productos')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${posTab === 'productos' ? 'bg-secondary text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>Productos</button>
                                </div>
                            </div>
                            
                            <div className="relative group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary-light transition-colors" size={18} />
                                <input 
                                    type="text" 
                                    className="input-field pl-12 pr-12 bg-slate-900/40 border-white/5 focus:border-primary-light/30 transition-all" 
                                    placeholder={`Buscar ${posTab}...`} 
                                    value={searchItem} 
                                    onChange={e => setSearchItem(e.target.value)} 
                                />
                                {searchItem && (
                                    <button 
                                        onClick={() => setSearchItem('')}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-red-400 p-1 transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>

                            <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-4 pr-2 custom-scrollbar">
                                {posTab === 'servicios' ? (
                                    servicios.filter(s => s.nombre.toLowerCase().includes(searchItem.toLowerCase())).map(s => (
                                        <button key={s.id} onClick={() => addToCart(s, 'servicio')} className="p-5 rounded-3xl bg-white/5 border border-white/5 hover:border-primary/50 hover:bg-primary/5 transition-all flex flex-col items-start gap-4 group">
                                            <div className="flex justify-between w-full h-12">
                                                <Zap size={24} className="text-primary-light group-hover:scale-125 transition-transform" />
                                                <span className="text-xl font-black text-white">{formatCurrency(s.precio_base)}</span>
                                            </div>
                                            <span className="font-bold text-sm leading-tight text-left">{s.nombre}</span>
                                        </button>
                                    ))
                                ) : (
                                    productos.filter(p => p.nombre.toLowerCase().includes(searchItem.toLowerCase())).map(p => (
                                        <button key={p.id} onClick={() => addToCart(p, 'producto')} className="p-5 rounded-3xl bg-white/5 border border-white/5 hover:border-secondary/50 hover:bg-secondary/5 transition-all flex flex-col items-start gap-4 group">
                                            <div className="flex justify-between w-full h-12">
                                                <Package size={24} className="text-secondary group-hover:scale-125 transition-transform" />
                                                <span className="text-xl font-black text-white">{formatCurrency(p.precio_venta)}</span>
                                            </div>
                                            <div className="text-left w-full">
                                                <span className="font-bold text-sm leading-tight block truncate">{p.nombre}</span>
                                                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">{p.stock} DISPONIBLES</span>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* COLUMNA DERECHA: CARRITO */}
                    <div className="glass bg-slate-900/60 rounded-[40px] p-10 border border-white/10 flex flex-col justify-between shadow-2xl">
                        <div className="overflow-hidden flex flex-col h-full">
                            <div className="flex justify-between items-center mb-10">
                                <h3 className="text-2xl font-black flex items-center gap-3"><ShoppingCart size={24} className="text-primary-light"/> Detalle de Orden</h3>
                                <span className="bg-white/5 px-4 py-2 rounded-2xl border border-white/5 text-xs font-black text-white/30 uppercase tracking-widest">{cart.length} Líneas</span>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto space-y-4 pr-3 custom-scrollbar">
                                {cart.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-5 bg-white/5 rounded-3xl border border-white/5 hover:border-white/10 transition-all group">
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-2xl ${item.type === 'producto' ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary-light'}`}>
                                                {item.type === 'producto' ? <Package size={18}/> : <Zap size={18}/>}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="font-bold text-sm text-white/90">{item.descripcion}</span>
                                                <span className="text-[10px] font-black uppercase text-white/20 tracking-widest">{item.type} • IVU: {item.aplica_ivu ? 'SÍ' : 'NO'}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <span className="text-lg font-black">{formatCurrency(item.precio_unitario)}</span>
                                            <button onClick={() => removeFromCart(idx)} className="text-white/10 hover:text-red-400 p-2 transition-colors"><Trash2 size={18}/></button>
                                        </div>
                                    </div>
                                ))}
                                {cart.length === 0 && (
                                    <div className="h-full flex flex-col items-center justify-center text-white/5 gap-4 opacity-50 space-y-2">
                                        <ShoppingCart size={80} strokeWidth={1} />
                                        <p className="text-sm font-bold uppercase tracking-widest italic">Carrito de Venta Vacío</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-10 pt-10 border-t border-white/5">
                            <h4 className="text-[10px] font-black uppercase text-white/20 tracking-[2px] mb-4">Forma de Recibo</h4>
                            <div className="grid grid-cols-3 gap-3">
                                {['Efectivo', 'Tarjeta de Crédito', 'ATH Móvil'].map(m => (
                                    <button key={m} onClick={() => setMetodoPago(m)} className={`py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${metodoPago === m ? 'bg-white text-slate-900 shadow-2xl scale-105' : 'glass text-white/30 border-white/5 hover:bg-white/5 hover:text-white'}`}>
                                        {m}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* PREVIEW MODAL */}
            <Modal 
                show={showPreview} 
                onClose={() => setShowPreview(false)} 
                title="Invoice Digital — PawSpa Pro" 
                size="lg"
                footer={(
                    <div className="flex flex-col md:flex-row gap-4 w-full">
                         <div className="flex-1 bg-white/5 px-6 py-4 rounded-2xl border border-white/10 flex items-center gap-4">
                            <Mail size={18} className="text-white/20" />
                            <input 
                                type="email" 
                                value={emailDestino} 
                                onChange={e => setEmailDestino(e.target.value)} 
                                className="bg-transparent border-none outline-none text-white font-bold w-full" 
                                placeholder="Email del cliente..." 
                            />
                         </div>
                         <button 
                            onClick={handleSendEmail}
                            disabled={enviandoEmail}
                            className={`${enviandoEmail ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/80'} bg-primary px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/20 transition-all flex items-center gap-3`}
                         >
                             {enviandoEmail ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div> : <Send size={16} />}
                             {enviandoEmail ? 'ENVIAR COMPROBANTE' : 'ENVIAR COMPROBANTE'}
                         </button>
                    </div>
                )}
            >
                <div className="w-full h-[55vh] rounded-3xl overflow-hidden glass border border-white/10 bg-slate-900/50 relative">
                    <iframe src={previewUrl} className="w-full h-full border-none hidden md:block" title="Preview PDF"></iframe>
                    
                    {/* Fallback Tablet */}
                    <div className="md:hidden flex flex-col items-center justify-center h-full p-6 text-center">
                        <FileText size={48} className="text-primary-light mb-4" />
                        <h3 className="text-lg font-black text-white mb-2">Visor de Factura</h3>
                        <p className="text-white/40 mb-6 text-xs font-medium">Su dispositivo requiere abrir la factura en una ventana independiente.</p>
                        <button 
                            onClick={() => window.open(previewUrl, '_blank')}
                            className="premium-btn-primary px-8 py-3 flex items-center gap-2 text-xs"
                        >
                            <Eye size={16} /> VER AHORA
                        </button>
                    </div>
                </div>
            </Modal>

            {/* QUICK CLIENT MODAL */}
            <Modal show={showQuickClient} onClose={() => setShowQuickClient(false)} title="Registro Express de Cliente">
                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-white/40">Nombre</label>
                            <input type="text" className="input-field" value={quickClientForm.nombre} onChange={e => setQuickClientForm({...quickClientForm, nombre: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-white/40">Apellido</label>
                            <input type="text" className="input-field" value={quickClientForm.apellido} onChange={e => setQuickClientForm({...quickClientForm, apellido: e.target.value})} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-white/40">Teléfono</label>
                        <input type="text" className="input-field" value={quickClientForm.telefono} onChange={e => setQuickClientForm({...quickClientForm, telefono: e.target.value})} placeholder="(787) 000-0000" />
                    </div>
                    <button onClick={handleQuickClientSave} className="premium-btn-primary w-full py-4 uppercase font-black tracking-widest text-xs">Registrar y Seleccionar</button>
                </div>
            </Modal>
        </div>
    );
};

export default Facturacion;
